import { BACKGROUND, WHITEBOARD_HEIGHT, WHITEBOARD_WIDTH, byId, drawItem, type Item } from "./whiteboard-items"

// Turns the whiteboard's pages into a PDF the therapist saves to their own
// device — one landscape page per board page. This is the only way anything
// on the board ever leaves the browser tab, and it goes to the therapist's
// downloads folder, never to the server: the file is assembled here from the
// display list and handed to the browser as a blob.
//
// The writer is deliberately tiny. A PDF page that is one JPEG image needs
// four objects and an xref table, and the canvas already produces the JPEG,
// so a PDF library would be a few hundred kilobytes for a page of boilerplate.

// Points, not pixels: the 1600×1000 board printed 20cm wide is a comfortable
// A4-landscape fit and keeps the file's own idea of size sane.
const PAGE_W = 800
const PAGE_H = 500
const JPEG_QUALITY = 0.92

// Paints a page's items onto any canvas at the board's own size. The live
// board must never be borrowed for this: it is the canvas the client is
// watching, and painting other pages onto it would flash them down the call.
export function paintPage(c: CanvasRenderingContext2D, items: Item[]) {
  c.fillStyle = BACKGROUND
  c.fillRect(0, 0, WHITEBOARD_WIDTH, WHITEBOARD_HEIGHT)
  const lookup = byId(items)
  for (const item of items) drawItem(c, item, lookup)
}

async function pageJpeg(items: Item[]): Promise<Uint8Array> {
  const canvas = document.createElement("canvas")
  canvas.width = WHITEBOARD_WIDTH
  canvas.height = WHITEBOARD_HEIGHT
  const c = canvas.getContext("2d")
  if (!c) throw new Error("no 2d context")
  paintPage(c, items)
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY))
  if (!blob) throw new Error("could not encode the page")
  return new Uint8Array(await blob.arrayBuffer())
}

const ascii = (s: string) => new TextEncoder().encode(s)

// Builds a PDF whose pages are the given JPEGs, each drawn to fill its page.
// Exported for the test: it is pure byte-shuffling and easiest to check
// from the outside.
export function pdfFromJpegs(images: Uint8Array[]): Uint8Array {
  const chunks: Uint8Array[] = []
  const offsets: number[] = []
  let length = 0
  const push = (bytes: Uint8Array) => {
    chunks.push(bytes)
    length += bytes.length
  }
  const object = (n: number, body: string, stream?: Uint8Array) => {
    offsets[n] = length
    push(ascii(`${n} 0 obj\n${body}\n`))
    if (stream) {
      push(ascii("stream\n"))
      push(stream)
      push(ascii("\nendstream\n"))
    }
    push(ascii("endobj\n"))
  }

  // Objects 1 and 2 are the catalogue and page tree; each page then takes
  // three consecutive numbers (page, contents, image) from 3 upwards.
  const pageNumbers = images.map((_, i) => 3 + i * 3)
  push(ascii("%PDF-1.4\n%âãÏÓ\n"))
  object(1, "<< /Type /Catalog /Pages 2 0 R >>")
  object(2, `<< /Type /Pages /Kids [${pageNumbers.map((n) => `${n} 0 R`).join(" ")}] /Count ${images.length} >>`)
  images.forEach((jpeg, i) => {
    const n = pageNumbers[i]
    object(
      n,
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] ` +
        `/Resources << /XObject << /Im0 ${n + 2} 0 R >> >> /Contents ${n + 1} 0 R >>`,
    )
    const content = ascii(`q ${PAGE_W} 0 0 ${PAGE_H} 0 0 cm /Im0 Do Q`)
    object(n + 1, `<< /Length ${content.length} >>`, content)
    object(
      n + 2,
      `<< /Type /XObject /Subtype /Image /Width ${WHITEBOARD_WIDTH} /Height ${WHITEBOARD_HEIGHT} ` +
        `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>`,
      jpeg,
    )
  })

  const count = 3 + images.length * 3
  const xref = length
  let table = `xref\n0 ${count}\n0000000000 65535 f \n`
  for (let n = 1; n < count; n++) table += `${String(offsets[n]).padStart(10, "0")} 00000 n \n`
  push(ascii(table))
  push(ascii(`trailer\n<< /Size ${count} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`))

  const out = new Uint8Array(length)
  let at = 0
  for (const chunk of chunks) {
    out.set(chunk, at)
    at += chunk.length
  }
  return out
}

// The file name carries the local date and time, so two sessions in a day
// do not overwrite each other in the downloads folder. No client name: the
// board knows nothing about who the session is with, and the file should not
// invent it.
export function notesFileName(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0")
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
  const time = `${pad(now.getHours())}${pad(now.getMinutes())}`
  return `whiteboard-notes-${date}-${time}.pdf`
}

// Renders every page and offers the PDF as a download.
export async function downloadNotes(pages: Item[][]) {
  const images: Uint8Array[] = []
  for (const items of pages) images.push(await pageJpeg(items))
  const bytes = pdfFromJpegs(images)
  const blob = new Blob([bytes as BlobPart], { type: "application/pdf" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = notesFileName()
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Revoking on the next tick rather than immediately: some browsers start
  // the download after click() returns and need the URL still valid.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
